import { Component, HostListener, Input, OnInit } from "@angular/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import { MapService } from "../../services/map.service";
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import { Feature, Map, Overlay, View } from "ol";
import { transform } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Icon, Stroke, Style } from "ol/style";
import { LineString, Point } from "ol/geom";
import {
  Coordinates,
  RequiredRouteInformation,
  RoutePointInformation,
  TooltipInformation
} from "../../models/map/required-route-information";
import { take } from "rxjs/operators";
import { createEmpty, extend, Extent } from "ol/extent";
import { FullScreen } from "ol/control";

@Component({
  selector: "map-modal",
  templateUrl: "./map-modal.component.html",
  styleUrls: ["./map-modal.component.scss"]
})
export class MapModalComponent extends SimpleModalComponent<void, void> implements OnInit {
  requiredRoutePointsInformation: RoutePointInformation[] = [];

  info = "";
  map!: Map;
  loadingMapDetails = true;
  private fullExtent: Extent;
  startIconColor = "#00FF00";
  stopIconColor = "cyan";

  private lineFeatures: Feature<LineString>[] = [];
  private iconFeatures: Feature<Point>[] = [];
  private highlightedLineFeatures = new Set();
  routeLineIndexClicked = -1;

  selectedRoutePoints: RoutePointInformation[] = [];

  showRouteDetailsTooltip = false;

  @HostListener("document:keydown", ["$event"])
  onKeydownHandler(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }

  constructor(private mapService: MapService) {
    super();
  }

  ngOnInit(): void {
    this.mapService.info.pipe(take(1)).subscribe(info => {
      this.info = info;
    });

    this.mapService.orderlines.pipe(take(1)).subscribe(ols => {
      this.requiredRoutePointsInformation = [];

      for (const ol of ols) {
        this.requiredRoutePointsInformation.push({
          coordinates: {
            x: ol.longitude,
            y: ol.latitude
          },
          tooltipInformation: {
            title: `${ol.actionNumber} - ${ol.address.name}`,
            informationLines: [
              ol.address.name2,
              `${ ol.address.street } ${ ol.address.addressNumber }`,
              `${ ol.address.countryCode }-${ ol.address.postalCode } ${ ol.address.city }`,
              ol.address.addressZoneName
            ]
          }
        });
      }

      this.mapService.fetchCoordinatesForEntireRoute(this.requiredRoutePointsInformation)
        .pipe(take(1))
        .subscribe((coordinatesList: Coordinates[][]) => {
          // CoordinatesList is a list of lists of coordinates
          // Each list of coordinates is a list of coordinates for a single route line
          // Each route line is a list of coordinates for a single route line
          // Each coordinate is a single point on the route line
          const startCoordinate = transform([coordinatesList[0][0].x, coordinatesList[0][0].y], "EPSG:4326", "EPSG:3857");

          this.map = new Map({
            layers: [new TileLayer({ source: new OSM() })],
            target: "map",
            view: new View({
              center: startCoordinate,
              zoom: 10,
              maxZoom: 20
            })
          });

          // Number to put on the 'stop icon' (1, 2, 3, etc.)
          let numberOnIcon = 2;

          for (let i = 0; i < coordinatesList.length; i++) {
            const routeLine = coordinatesList[i];

            // DRAW START ICON (ONLY IF THERE ARE MORE THAN 1 SET OF COORDINATES OTHERWISE END ICON IS USED)
            if (i === 0 && routeLine.length > 1) {
              const coordinate = routeLine[0];
              const startIconFeature = this.createPointFeatureWithIcon(
                coordinate,
                this.requiredRoutePointsInformation[0].tooltipInformation,
                () => this.createIconSvgDataURL(this.startIconColor, 1)
              );

              this.iconFeatures.push(startIconFeature);
            }

            // DRAW END ICON
            if (i === coordinatesList.length - 1) {
              const coordinate = routeLine[routeLine.length - 1];

              // requiredRouteInformation: Just need the last route point information
              const endIconFeature = this.createPointFeatureWithIcon(
                coordinate,
                this.requiredRoutePointsInformation[this.requiredRoutePointsInformation.length - 1].tooltipInformation,
                () => "/assets/img/map/end-pin-icon.svg"
              );

              this.iconFeatures.push(endIconFeature);
            } else {
              // DRAW STOP ICONS
              for (let j = 0; j < routeLine.length; j++) {
                if (j === routeLine.length - 1) {
                  const stopIconFeature = this.createPointFeatureWithIcon(
                    routeLine[j],
                    this.requiredRoutePointsInformation[i + 1].tooltipInformation,
                    () => this.createIconSvgDataURL(this.stopIconColor, numberOnIcon)
                  );

                  this.iconFeatures.push(stopIconFeature);

                  // Only increase the number if a number is used (number inside the icon)
                  numberOnIcon++;
                }
              }
            }

            // DRAWING THE ROUTE LINE (IF THERE ARE MORE THAN 1 SET OF COORDINATES)
            const lineFeature = this.createLineString(routeLine);
            this.lineFeatures.push(lineFeature);
          }


          // ADD ALL ROUTE LINE FEATURES TO MAP
          const vectorSourceDrivingRoute = new VectorSource({
            features: this.lineFeatures
          });

          const routeLineLayer = new VectorLayer({
            source: vectorSourceDrivingRoute,
            style: this.getDefaultRouteLineStyle()
          });

          this.map.addLayer(routeLineLayer);


          // ADD ALL ICON LAYERS TO MAP
          const vectorSourceIconFeatures = new VectorSource({
            features: this.iconFeatures
          });

          const pointIcons = new VectorLayer({ source: vectorSourceIconFeatures });

          this.map.addLayer(pointIcons);


          this.addTooltip();


          // FIT MAP TO CONTAIN ALL FEATURES
          const fullExtent = createEmpty();

          for (const layer of this.map.getLayers().getArray()) {
            if (layer instanceof VectorLayer) {
              const features = layer.getSource().getFeatures();

              for (const feature of features) {
                extend(fullExtent, feature.getGeometry().getExtent());
              }
            }
          }

          const height = fullExtent[3] - fullExtent[1];
          const additionalTopBuffer = height * 0.1; // 10% buffer at the top so the icon fits inside the map

          fullExtent[3] += additionalTopBuffer;

          // Used for centering the map on a click of the center button
          this.fullExtent = fullExtent;

          this.map.getView().fit(fullExtent, { padding: [30, 30, 30, 30], maxZoom: 20 });

          this.map.addControl(new FullScreen());


          this.handleClickCenterButton();


          this.loadingMapDetails = false;
        });


      // Add All Route Points to start as selected
      this.selectedRoutePoints = this.requiredRoutePointsInformation;
    });


  }

  closeModal() {
    this.close();
    this.mapService.toggleMap();
  }

  private addTooltip() {
    const tooltip = document.getElementById("map-tooltip");

    const tooltipOverlay = new Overlay({
      element: tooltip,
      positioning: "bottom-center",
      stopEvent: false,
      offset: [25, -15] // Position the tooltip so the cursor is not directly over it
    });

    this.map.addOverlay(tooltipOverlay);

    this.map.on("pointermove", (evt) => {
      const feature = this.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);

      // Avoid showing tooltip on the line, only show it on the icons
      if (feature?.get("title")) {
        this.map.getTargetElement().style.cursor = "help";

        const title = feature.get("title");
        const description = feature.get("description");
        tooltip.innerHTML = `<strong>${ title }</strong><hr style="margin-top: 4px; margin-bottom: 8px;">${ description }`;

        tooltipOverlay.setPosition(evt.coordinate);
        tooltip.style.opacity = "1";
      } else {
        this.map.getTargetElement().style.cursor = "";
        tooltip.style.opacity = "0";
      }
    });
  }


  private createLineString(coordinates: Coordinates[]) {
    const mapSpecificCoordinates = coordinates.map(coord => transform([coord.x, coord.y], "EPSG:4326", "EPSG:3857"));

    const lineString = new LineString(mapSpecificCoordinates);

    return new Feature(lineString);
  }

  private createPointFeatureWithIcon(coordinate: Coordinates, tooltipInformation: TooltipInformation, createIcon: () => string) {
    const feature = new Feature({
      type: "icon",
      geometry: new Point(transform([coordinate.x, coordinate.y], "EPSG:4326", "EPSG:3857")),
      title: tooltipInformation.title,
      description: tooltipInformation.informationLines.filter(line => line?.trim().length > 0).join("<br>")
    });

    const startPinStyle = new Style({
      image: new Icon({
        src: createIcon(),
        scale: 1,
        anchor: [0.5, 1], // This makes the bottom of the icon point to the exact coordinate
        anchorXUnits: "fraction",
        anchorYUnits: "fraction"
      })
    });

    feature.setStyle(startPinStyle);

    return feature;
  }

  private createIconSvgDataURL(color: string, number: number) {
    let xPosition = 218;
    let radius = 60;
    let fontSize = 100;

    if (number > 9) {
      radius = 70;
      xPosition = 192;
      fontSize = 90;
    }

    const svgContent = `
    <svg width="64" height="64" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
        <path fill="${ color }" d="M336 96a80 80 0 1 0-96 78.39v283.17a32.09 32.09 0 0 0 2.49 12.38l10.07 24a3.92 3.92 0 0 0 6.88 0l10.07-24a32.09 32.09 0 0 0 2.49-12.38V174.39A80.13 80.13 0 0 0 336 96Zm-56 0a24 24 0 1 1 24-24a24 24 0 0 1-24 24Z" stroke="black" stroke-width="10"/>
        <circle cx="257" cy="95" r="${ radius }" fill="${ color }" />
        <text x="${ xPosition }" y="130" fill="black" font-family="Verdana" font-size="${ fontSize }" font-weight="bold">${ number }</text>
    </svg>`;

    return "data:image/svg+xml," + encodeURIComponent(svgContent);
  }

  highlightRoute(indexOfRouteLine: number) {
    if (this.loadingMapDetails) {
      return;
    }

    this.routeLineIndexClicked = indexOfRouteLine;

    // Set all icons to lower opacity
    for (const feature of this.iconFeatures) {
      const featureStyle = feature.getStyle();

      if (featureStyle instanceof Style) {
        const iconStyle = featureStyle.getImage();

        if (iconStyle instanceof Icon) {
          // Update the opacity
          iconStyle.setOpacity(0.3);

          // Reapply the style to the feature
          feature.setStyle(
            new Style({
              image: iconStyle,
            })
          );
        }
      }
    }

    // Set the selected route line icons to full opacity
    const startIconFeature = this.iconFeatures[indexOfRouteLine];
    const endIconFeature = this.iconFeatures[indexOfRouteLine + 1];

    for (const feature of [startIconFeature, endIconFeature]) {
      const featureStyle = feature.getStyle();

      if (featureStyle instanceof Style) {
        const iconStyle = featureStyle.getImage();

        if (iconStyle instanceof Icon) {
          iconStyle.setOpacity(1);

          feature.setStyle(
            new Style({
              image: iconStyle,
            })
          );
        }
      }
    }


    for (const feature of this.lineFeatures) {
      const indexOfClickedLineFeature = this.lineFeatures.indexOf(feature);

      if (indexOfClickedLineFeature === indexOfRouteLine) {
        feature.setStyle(this.getDefaultRouteLineStyle(100, 1, 5));

        if (this.highlightedLineFeatures.has(feature)) {
          // Stop highlighting
          this.highlightedLineFeatures.delete(feature);
        } else {
          // Start highlighting
          this.highlightedLineFeatures.add(feature);
        }
      } else {
        // Set not selected to lower opacity
        feature.setStyle(this.getDefaultRouteLineStyle(1, 0.3));

        this.highlightedLineFeatures.delete(feature);
      }
    }

    // Reset all line routes style
    if (this.highlightedLineFeatures.size === 0) {
      for (const feature of this.lineFeatures) {
        feature.setStyle(this.getDefaultRouteLineStyle());
      }

      // Reset all icons to full opacity
      for (const feature of this.iconFeatures) {
        const featureStyle = feature.getStyle();

        if (featureStyle instanceof Style) {
          const iconStyle = featureStyle.getImage();

          if (iconStyle instanceof Icon) {
            iconStyle.setOpacity(1);

            feature.setStyle(
              new Style({
                image: iconStyle,
              })
            );
          }
        }
      }

      this.routeLineIndexClicked = -1;

      this.selectedRoutePoints = this.requiredRoutePointsInformation;
    }
  }

  private getDefaultRouteLineStyle(zIndex = 1, opacity = 1, strokeWidth = 3) {
    return new Style({
      zIndex: zIndex,
      stroke: new Stroke({
        color: [0, 0, 255, opacity],
        width: strokeWidth
      })
    });
  }

  handleRouteDetailsTooltip(show: boolean) {
    this.showRouteDetailsTooltip = show;
  }

  private handleClickCenterButton() {
    document.querySelector("#center-button button").addEventListener("click", () => {
      this.map.getView().fit(this.fullExtent, { padding: [30, 30, 30, 30], maxZoom: 20 });
    });
  }
}
